import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import { ClickEventComponentNames, CustomEventNames } from 'features/analytics';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry } from 'features/cart/types';
import { logButtonClick } from 'features/navigation/utils/analytics';
import {
  findOfferListItemIndex,
  logOfferEvent,
  logOffersDisplayed,
  offerListPositionParams,
} from 'features/offer/utils/analytics';
import { isEmpty } from 'lodash-es';
import { FlatList, StyleSheet, View } from 'react-native';

import ConfettiFriesAnimation from 'components/lottie-animations/confetti-fries';
import { useConfettiFriesAnimation } from 'components/lottie-animations/confetti-fries/use-confetti-fries-animation';
import {
  PullToReloadScrollView,
  pullToReloadStyles,
  usePullToReload,
} from 'components/pull-to-reload/pull-to-reload';
import { toaster } from 'components/toaster/toaster';
import { centeredRowContainerStyles } from 'components/ui/centered-row-container';
import { OtherAvailableOffersHeader } from 'experiments/deeplinks-flow-feature/treatment/components/offer-list-item/components/other-available-offers-header';
import { useUnavailableOfferRecovery } from 'experiments/deeplinks-flow-feature/treatment/features/offer/hooks/use-unavailable-offer-recovery';
import {
  buildUnavailableOfferRecoveryList,
  getOtherAvailableOffersHeaderIndex,
} from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/build-unavailable-offer-recovery-list';
import { useOffersPersonalizationExperiment } from 'experiments/offers-personalization/use-offers-personalization-experiment';
import { useCart } from 'hooks/use-cart';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useNativeTabBarContentInset } from 'hooks/use-native-tab-bar-content-inset';
import OffersEmptyScreen from 'pages/loyalty/loyalty-offers/empty-screen/empty-screen';
import EndOfOffers from 'pages/loyalty/loyalty-offers/end-of-offers/loyalty-end-of-offers';
import { LoyaltyListItem } from 'pages/loyalty/loyalty-offers/loyalty-list-item';
import { LoyaltyOffersSkeletonLoader } from 'pages/loyalty/loyalty-offers/loyalty-offers-skeleton-loader';
import { LoyaltyPromoCodeCTA } from 'pages/loyalty/loyalty-offers/loyalty-promo-code/loyalty-promo-code-cta';
import { OffersInCart } from 'pages/loyalty/loyalty-offers/offers-in-cart/offers-in-cart';
import useLoyaltyOffersFlow from 'pages/loyalty/loyalty-offers/use-loyalty-offers-flow';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger } from 'state/auth/types';
import { DISCOUNT_OFFER_CART_ID } from 'state/cart/constants';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyContext } from 'state/loyalty';
import { LoyaltyOfferWithInlineAlertMessage } from 'state/loyalty/hooks/types';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { AppOfferListItem, LoyaltyOffer, isAppOffer } from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';
import { useNetworkContext } from 'state/network';
import { spacing } from 'styles/constants';
import { TRANSITION_ANIMATION_TIME_MS, WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { routes } from 'utils/routing';
import { stringToBool } from 'utils/string-To-Bool';
import { waitForDuration } from 'utils/wait-for-duration';

import { useOffersPresentationState } from '../hooks/use-offers-presentation-state';
import { useServiceIncentive } from '../hooks/use-service-incentive';
import { useEnableServiceOffersBrowse, useServiceOffers } from '../state/service-offers-context';
import { personalizeOfferListItems } from '../utils/personalize-offer-list-items';
import { sortOfferListItemsByAvailability } from '../utils/sort-offer-list-items';

import { LoyaltyServicePromoCodeDrawer } from './loyalty-service-promo-code-drawer';
import { OffersLoadRecovery } from './offers-load-recovery';

export const OffersBase = () => {
  const { loyaltyStandardOffersEnabled } = useLoyaltyContext();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { setModalAuthState } = useAuthContext();
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const dispatch = useAppDispatch();
  const {
    inRestaurantRedemptionCart,
    removeInRestaurantRedemptionEntry,
  } = useInRestaurantRedemptionContext();
  const [authRequested, setAuthRequested] = useState(false);

  const enableLoyaltyPromoCodes = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROMO_CODES);
  const { removeFromCart } = useCart();
  const { removeItemFromMinimalCart } = useMinimalCart();

  const router = useRouter();

  const {
    loyaltyEngineId,
    deeplinkUnavailableOfferEngineId,
    deeplinkUnavailableReason,
  } = useLoyaltyOffersFlow();
  const skipAnimation = useRef(true);

  const { triggerAnimation } = useConfettiFriesAnimation();
  const queryParams = useLocalSearchParams<{ success?: string; promoCode?: string }>();
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(
    !!stringToBool(queryParams.success)
  );

  const [isPromoCodeOpen, _setIsPromoCodeOpen] = useState(false);

  const openPromoCodeDrawer = useCallback(() => {
    setTimeout(() => {
      _setIsPromoCodeOpen(true);
    }, TRANSITION_ANIMATION_TIME_MS);
  }, []);

  const closePromoCodeDrawer = useCallback(() => {
    _setIsPromoCodeOpen(false);
  }, []);

  useFocusEffect(
    useCallback(() => {
      if (queryParams.promoCode && !authRequested) {
        openPromoCodeDrawer();
      }
    }, [openPromoCodeDrawer, queryParams.promoCode, authRequested])
  );

  const shouldDisplayPromoCodeDrawer = useRef(false);

  useLoyaltyUserState({
    refreshOnMountOrFocus: true,
    onSuccess: () => {
      if (shouldDisplayPromoCodeDrawer.current) {
        openPromoCodeDrawer();
        shouldDisplayPromoCodeDrawer.current = false;
      }
    },
  });

  const onDismissPromoDrawer = useCallback(() => {
    router.setParams({ promoCode: '' });
    closePromoCodeDrawer();
  }, [closePromoCodeDrawer, router]);

  const handleAuthenticationRequired = (promoCode: string) => {
    router.setParams({ promoCode });
    setAuthRequested(true);
    closePromoCodeDrawer();

    setTimeout(() => {
      setModalAuthState({
        screen: ModalAuthScreen.SIGN_IN,
        trigger: ModalAuthTrigger.Global,
        onClose: () => {
          setAuthRequested(false);
          openPromoCodeDrawer();
        },
        onSignInSuccess: () => {
          shouldDisplayPromoCodeDrawer.current = true;
        },
      });
    });
  };

  // Run the browse-list query only while the Offers list is mounted
  useEnableServiceOffersBrowse();

  const {
    offerListItems,
    offersInCart,
    personalizedOfferGroups,
    reprocessOffers,
    refreshOffers,
    loading: offersAreLoading,
    error: offersError,
    hasSettled = !offersAreLoading,
    blockers = [],
    storeId,
    serviceMode,
  } = useServiceOffers();
  const { isInternetReachable } = useNetworkContext();
  const presentationState = useOffersPresentationState({
    loading: offersAreLoading,
    error: offersError,
    hasSettled,
    hasContent: offerListItems.length > 0,
    isInternetReachable: isInternetReachable !== false,
    blockers: [...blockers],
    runtime: 'loyalty-service',
    enteredWithPromoCode: !!queryParams.promoCode,
    storeId,
    serviceMode,
  });

  const {
    isPending: isOffersPersonalizationPending,
    isTreatment: isOffersPersonalizationTreatment,
    sortedSystemwideOffers,
    tierGroupChildIdsByParentId,
    tierGroupLookupHasError,
  } = useOffersPersonalizationExperiment();

  const offersListIsLoading = offersAreLoading || isOffersPersonalizationPending;

  useEffect(() => {
    dispatch(actions.loyalty.setAppPersonalizedOfferGroups(personalizedOfferGroups));
  }, [dispatch, personalizedOfferGroups]);

  const { selectedIncentive } = useServiceIncentive({
    loyaltyEngineId,
    offerListItems,
    offersInCart,
  });
  const {
    unavailableOffer: deeplinkUnavailableOffer,
    isRecovering: isRecoveringDeeplinkOffer,
    recoveryError: deeplinkRecoveryError,
    retryRecovery: retryDeeplinkRecovery,
  } = useUnavailableOfferRecovery(deeplinkUnavailableOfferEngineId, deeplinkUnavailableReason);

  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const removeLoyaltyOffer = useCallback(
    (offerToRemove: LoyaltyOffer) => {
      const appliedOfferToRemove = appliedOffers.find(
        offer => offer.id === offerToRemove.loyaltyEngineId
      );

      if (appliedOfferToRemove?.cartId) {
        dispatch(actions.loyalty.removeAppliedOffer(appliedOfferToRemove));
        removeFromCart({ cartId: appliedOfferToRemove.cartId });
        return true;
      }

      return false;
    },
    [appliedOffers, dispatch, removeFromCart]
  );

  const handleRemove = useCallback(
    (offerToRemove: LoyaltyOffer) => {
      const redemptionOfferToRemove = inRestaurantRedemptionCart.find(
        cartEntry =>
          cartEntry.type === ICartEntryType.OFFER &&
          cartEntry.details.offer._id === offerToRemove._id
      );

      let showNotification = false;

      if (redemptionOfferToRemove) {
        removeInRestaurantRedemptionEntry(redemptionOfferToRemove);
        showNotification = true;
      } else if (isDiscountLoyaltyOffer(offerToRemove)) {
        if (isUnifiedMenuEnabled) {
          removeItemFromMinimalCart({ cartId: DISCOUNT_OFFER_CART_ID } as ICartEntry);
        }
        removeFromCart({ cartId: DISCOUNT_OFFER_CART_ID });
        dispatch(actions.loyalty.setSelectedOffer(null));
        dispatch(actions.loyalty.resetAppliedDiscountOffers());
        reprocessOffers();
        showNotification = true;
      } else {
        reprocessOffers();
        showNotification = removeLoyaltyOffer(offerToRemove);
      }

      if (showNotification) {
        toaster.positive(t('offerAppliedSuccess'));
      }
    },
    [
      inRestaurantRedemptionCart,
      removeInRestaurantRedemptionEntry,
      isUnifiedMenuEnabled,
      removeFromCart,
      dispatch,
      reprocessOffers,
      removeItemFromMinimalCart,
      removeLoyaltyOffer,
    ]
  );

  useEffect(() => {
    if (shouldRefetchOffers) {
      refreshOffers();
    }
  }, [refreshOffers, shouldRefetchOffers]);

  useEffect(() => {
    if (offersInCart?.length && !offerListItems.length) {
      dispatch(actions.loyalty.setShouldRefetchOffers(true));
    }
  }, [offersInCart?.length, offerListItems.length, dispatch]);

  const treatmentOfferListItems = useMemo(() => {
    if (!isOffersPersonalizationTreatment || !sortedSystemwideOffers || tierGroupLookupHasError) {
      return;
    }

    return personalizeOfferListItems(
      offerListItems,
      sortedSystemwideOffers,
      tierGroupChildIdsByParentId
    );
  }, [
    isOffersPersonalizationTreatment,
    offerListItems,
    sortedSystemwideOffers,
    tierGroupChildIdsByParentId,
    tierGroupLookupHasError,
  ]);

  const displayOfferListItems = useMemo(() => {
    const items = treatmentOfferListItems?.hasMatchedItems
      ? treatmentOfferListItems.items
      : sortOfferListItemsByAvailability(offerListItems);

    return buildUnavailableOfferRecoveryList(items, deeplinkUnavailableOffer);
  }, [deeplinkUnavailableOffer, offerListItems, treatmentOfferListItems]);

  const otherAvailableOffersHeaderIndex = useMemo(
    () => getOtherAvailableOffersHeaderIndex(displayOfferListItems, deeplinkUnavailableOffer),
    [deeplinkUnavailableOffer, displayOfferListItems]
  );

  const handleOfferPress = useCallback(
    (incentive: LoyaltyOfferWithInlineAlertMessage) => {
      if (incentive.inlineAlertMessage) {
        return;
      }
      const incentiveId = incentive._id;
      const loyaltyEngineId = incentive.loyaltyEngineId;
      if (incentiveId !== selectedIncentive?._id) {
        logOfferEvent({
          name: CustomEventNames.OFFER_SELECTED,
          incentives: [incentive],
          attributes: {
            serviceMode: incentive.offerServiceMode,
          },
        });

        const offerIndex = findOfferListItemIndex(displayOfferListItems, {
          kind: 'offer',
          loyaltyEngineId: loyaltyEngineId ?? '',
        });

        router.navigate({
          pathname: '/offers/[loyaltyEngineId]',
          params: {
            loyaltyEngineId,
            ...offerListPositionParams(offerIndex),
          },
        });
      }
    },
    [displayOfferListItems, router, selectedIncentive]
  );

  const handleGroupPress = useCallback(
    (offerGroup: { cmsId: string; id?: string }) => {
      const groupIndex = findOfferListItemIndex(displayOfferListItems, {
        kind: 'group',
        cmsId: offerGroup.cmsId,
        id: offerGroup.id,
      });

      router.navigate({
        pathname: '/offers/group/[offerGroupId]',
        params: {
          offerGroupId: offerGroup.cmsId,
          ...(offerGroup.id ? { loyaltyEngineId: offerGroup.id } : {}),
          ...offerListPositionParams(groupIndex),
        },
      });
    },
    [displayOfferListItems, router]
  );

  useEffect(() => {
    if (displayOfferListItems.length > 0 && !offersListIsLoading) {
      logOffersDisplayed(displayOfferListItems);
    }
  }, [displayOfferListItems, offersListIsLoading]);

  useEffect(() => {
    if (
      loyaltyEngineId &&
      !deeplinkUnavailableOffer?.inlineAlertMessage &&
      !selectedIncentive &&
      loyaltyStandardOffersEnabled &&
      !offersListIsLoading &&
      !isEmpty(offerListItems)
    ) {
      toaster.negative(t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }));
    }
  }, [
    offerListItems,
    offersListIsLoading,
    selectedIncentive,
    loyaltyEngineId,
    deeplinkUnavailableOffer?.inlineAlertMessage,
    loyaltyStandardOffersEnabled,
  ]);

  const renderItem = useCallback(
    ({ item, index }: { item: AppOfferListItem; index: number }) => {
      return (
        <>
          {index === otherAvailableOffersHeaderIndex ? <OtherAvailableOffersHeader /> : null}
          <LoyaltyListItem
            item={item}
            handleOfferPress={handleOfferPress}
            handleGroupPress={handleGroupPress}
            skipAnimation={skipAnimation.current}
            deeplinkHighlightedOfferEngineId={deeplinkUnavailableOffer?.loyaltyEngineId}
          />
        </>
      );
    },
    [
      deeplinkUnavailableOffer?.loyaltyEngineId,
      handleGroupPress,
      handleOfferPress,
      otherAvailableOffersHeaderIndex,
    ]
  );

  const onPromoCodeRedeemSuccess = useCallback(
    (loyaltyEngineId: string) => {
      refreshOffers();
      toaster.positive(t('promoCodeAddedToOffers'));
      router.navigate(routes.loyaltyOfferDetails.replace(':loyaltyEngineId', loyaltyEngineId));
    },
    [refreshOffers, router]
  );

  const canShowPromoCode = enableLoyaltyPromoCodes && !selectedIncentive;

  const ListHeaderComponent = useCallback(() => {
    const onOpenPromoDrawer = () => {
      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PROMO_CODE_TILE,
          name: 'Have a Promo Code?',
        },
      });
      openPromoCodeDrawer();
    };

    return (
      <>
        {canShowPromoCode && <LoyaltyPromoCodeCTA onPress={onOpenPromoDrawer} />}
        {offersInCart?.length ? (
          <OffersInCart
            offers={offersInCart}
            handlePress={handleOfferPress}
            handleRemove={handleRemove}
          />
        ) : null}
      </>
    );
  }, [canShowPromoCode, handleOfferPress, handleRemove, offersInCart, openPromoCodeDrawer]);

  const onRefresh = async () => {
    await waitForDuration(1700);
    refreshOffers();
  };

  const { pullToReloadSpinner, pullToReloadProps } = usePullToReload({
    progressViewOffset: 10,
    onRefresh,
  });
  const nativeTabBarContentInset = useNativeTabBarContentInset();

  useEffect(() => {
    if (showSuccessAnimation && loyaltyStandardOffersEnabled && !offersAreLoading) {
      triggerAnimation();
      toaster.positive(t('walmartEnjoyYourOffers'));
      setShowSuccessAnimation(false);
      router.setParams({ success: undefined });
    }
  }, [
    loyaltyStandardOffersEnabled,
    router,
    showSuccessAnimation,
    offersAreLoading,
    triggerAnimation,
  ]);

  if (!loyaltyStandardOffersEnabled) {
    return null;
  }

  return (
    <>
      {deeplinkRecoveryError ||
      presentationState.status === 'waiting-for-network' ||
      presentationState.status === 'error' ? (
        <OffersLoadRecovery
          offline={!deeplinkRecoveryError && presentationState.status === 'waiting-for-network'}
          onRetry={deeplinkRecoveryError ? retryDeeplinkRecovery : refreshOffers}
        />
      ) : presentationState.status === 'initial-loading' ||
        isOffersPersonalizationPending ||
        isRecoveringDeeplinkOffer ? (
        <LoyaltyOffersSkeletonLoader />
      ) : displayOfferListItems.length ? (
        <>
          <View style={pullToReloadStyles.scrollViewContainer}>
            {pullToReloadSpinner}
            <FlatList
              scrollEventThrottle={16}
              ListHeaderComponent={ListHeaderComponent}
              ListFooterComponent={EndOfOffers}
              contentContainerStyle={[
                styles.flatList,
                nativeTabBarContentInset ? { paddingBottom: nativeTabBarContentInset } : null,
              ]}
              data={displayOfferListItems}
              initialNumToRender={5}
              keyExtractor={(item, index) => {
                if (isAppOffer(item)) {
                  return item.loyaltyEngineId;
                }
                if ('cmsId' in item && item.cmsId) {
                  return item.cmsId;
                }
                return `${index}`;
              }}
              renderItem={renderItem}
              {...pullToReloadProps}
            />
          </View>
          <ConfettiFriesAnimation />
        </>
      ) : (
        <PullToReloadScrollView onRefresh={onRefresh} progressViewOffset={10}>
          <OffersEmptyScreen />
        </PullToReloadScrollView>
      )}
      {isPromoCodeOpen && (
        <LoyaltyServicePromoCodeDrawer
          isOpen={isPromoCodeOpen}
          onDismiss={onDismissPromoDrawer}
          promoCode={queryParams.promoCode?.toString()}
          onPromoCodeRedeemSuccess={onPromoCodeRedeemSuccess}
          onAuthenticationRequired={handleAuthenticationRequired}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  flatList: {
    ...centeredRowContainerStyles.content,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
  },
});
